/**
 * CreateUserDialogComponent
 * Author: Prajna
 * MOdified Date:01/01/2025
 * Version: 1.0.0
 * 
 * Description:
 * This component provides a dialog interface for creating and editing user accounts.
 * It allows administrators to manage user details, including assigning organizations
 * and setting roles, statuses, and other relevant properties.
 * 
 * Key Features:
 * - Create new user accounts.
 * - Edit existing user details.
 * - Assign users to one or more organizations.
 * - Validate unique email addresses before saving.
 * - Manage roles and statuses based on logged-in user's privileges.
 * 
 * Dependencies:
 * - Angular Material Dialog (`MatDialogRef` and `MAT_DIALOG_DATA`)
 * - UserService for API interactions.
 * - OrganizationService for fetching and assigning organizations.
 * - AuthService for user role-based permissions.
 */
import { Component, OnInit, ChangeDetectionStrategy, Inject, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserService } from 'src/app/services/user.service';
import { OrganizationService } from '../organization/organization.service';
import { Organization } from 'src/app/services/organization.model';
import { forkJoin, Observable } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { UserDataService } from 'src/app/services/user-data-service';

@Component({
    selector: 'app-create-user-dialog',
    templateUrl: './create-user-dialog.component.html',
    styleUrls: ['./create-user-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreateUserDialogComponent implements OnInit {
  public userdata: any;
  public userorgdata: any;
  dialogTitle: string;
  currentEmail: string;
  newInputEmail: string;
  organizations: Organization[] = [];
  hasError: boolean = false;
  selectedOrganizationIds: number[] = []; // Define selectedOrganizationIds here
  isPlaceholderVisible: boolean = true;
  showPassword: boolean = false; // Password visibility toggle
  organizationId:number=0;
  isSaving = false;
  userTypes = [
    { id: 1, label: 'Super Admin' },
    { id: 2, label: 'Admin' },
    { id: 3, label: 'Analyst' },
    { id: 4, label: 'Operator' }
  ];
  role: number; // To hold the user's role
  filteredUserTypes = this.userTypes; // Default to show all options

  statusOptions = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'In-Active' }
  ];

  phonePattern = '^((\\+\\d{1,3}[- ]?)?\\(?\\d{1,4}\\)?[- ]?\\d{1,4}[- ]?\\d{1,4}[- ]?\\d{1,4}|\\(?\\d{3}\\)?[- ]?\\d{3}[- ]?\\d{3})$';
  filteredOrganizations = [];
 
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private organizationService: OrganizationService,
    private cdRef: ChangeDetectorRef,
    private authService: AuthService,
     private userData: UserDataService,
     
    private dialogRef: MatDialogRef<CreateUserDialogComponent>
  ) {
    this.dialogTitle = data?.title ?? 'Create User';
    this.currentEmail=data?.user ?.emailAddress;
    this.userdata = data?.user ? { ...data.user } : this.initializeEmptyUserData();
    this.organizationId=this.userData.getSelectedOrganization();
   // console.log("org id==",this.organizationId);
    // Pre-populate organizations if editing a user
    if (this.userdata.organization) {
      this.userdata.organizations = this.userdata.organization.map(org => org.organizationId);
      this.userdata.organizationsTranctionid = this.userdata.organization.map(org => org.id);
    } else {
      this.userdata.organizations = []; // For new users
      this.userdata.organizationsTranctionid=[];
    }
    console.log( "user data===",this.userdata);
     console.log( "user Type===",this.userdata.userType);
    console.log("transction ids", this.userdata.organizationsTranctionid );
  }
   ngOnInit(): void {
    const user = this.authService.getUserDetails();
    // Check if user exists and set the role
    if (user) {
      this.role = user.userType;
      // If the userType is not '1' (Super Admin), filter out the Super Admin option
     // if (this.role !== 1) {
      //  this.filteredUserTypes = this.userTypes.filter(userType => userType.id !== 1);
      //}
      this.filterUserTypesByRole();
    }
    this.loadOrganizations();
    // Optionally trigger change detection here if necessary
    this.cdRef.detectChanges();
  }

    filterUserTypesByRole(): void {
      if (this.role === 1) {
        // If organizationId exists, show all user types; otherwise, show only Super Admin
        this.filteredUserTypes = this.organizationId 
          ? this.userTypes 
          : this.userTypes.filter(userType => userType.id === 1);
      } else {
        // Show all user types except Super Admin
        this.filteredUserTypes = this.userTypes.filter(userType => userType.id !== 1);
      }
    }
    

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userdata'] && this.userdata.organizations.length > 0) {
      this.cdRef.detectChanges(); // Ensure the changes are reflected in the view
    }
  }
  private initializeEmptyUserData() {
    return {
      userId: '',
      userName: '',
      password: '',
      firstName: '',
      lastName: '',
      emailAddress: '',
      userType: '',
      primaryPhone: '',
      title: '',
      notes: '',
      status: 1,
      organizations: []  // To hold the selected organizations
    };
  }

  togglePlaceholder() {
    this.isPlaceholderVisible = this.userdata.organizations.length === 0;
  }

  onSave(): void {
   if (this.isSaving) return; // prevent double clicks
  this.isSaving = true;
  const isEditMode = !!this.data.user; // Determine if in edit mode or create mode
 // this.createOrUpdateUser(isEditMode);
//console.log("isEditMode=",isEditMode);
  // Step 2: If it is edit mode, check if organizationsTranctionid exists (indicating that user organizations need to be deleted)
 if(this.userdata.userType)
 {
 this.createOrUpdateUser(isEditMode);
 }
 else
 {
if (isEditMode && this.userdata.organizationsTranctionid) {
    this.deleteUserOrganizations(this.userdata.organizationsTranctionid).subscribe(
      () => {
        console.log('User organizations deleted successfully.');

        // Continue with user create or update after deletion
        this.createOrUpdateUser(isEditMode);
      },
      (error) => {
        console.error('Error deleting user organizations:', error);
        // Optionally, show an error message if needed
        this.dialogRef.close({ status: false, message: error.message || 'An error occurred while deleting organizations.' });
      }
    );
  } else {
    // If not in edit mode or no organizations to delete, continue directly with user create or update
    this.createOrUpdateUser(isEditMode);
  }
 }
  
  }
 /* async createOrUpdateUser(isEditMode: boolean): Promise<void> {
    try {
      // Step 1: Determine whether to update or create the user
      const userResponse = isEditMode
        ? await this.userService.updateUser(this.userdata)
        : await this.userService.createUser(this.userdata);
  
      console.log(isEditMode ? 'User updated successfully:' : 'User created successfully:', userResponse);
  
      // Step 2: Assign user to organizations after creating or updating the user
      for (const orgId of this.userdata.organizations) {
        const userOrgData = {
          userId: isEditMode ? this.userdata.userId : userResponse.id,
          organizationId: orgId
        };
  
        try {
          await this.userService.assignOrganization(userOrgData).toPromise();
          console.log(`User assigned to organization ${orgId} successfully.`);
        } catch (error) {
          console.error(`Error assigning user to organization ${orgId}:`, error);
        }
      }
  
      // Step 3: Close the dialog with the user response
      this.dialogRef.close({ 
        status: true, 
        user: userResponse,  // Pass the userResponse back when closing the dialog
        message: isEditMode ? 'User updated successfully' : 'User created successfully'  // Optional message
      });
    } catch (error) {
      console.error(isEditMode ? 'Error updating user:' : 'Error creating user:', error);
      this.dialogRef.close({
        status: false,
        message: error.message || 'An error occurred.' // Pass error message if any
      });
    }
  }
  */
  async createOrUpdateUser(isEditMode: boolean): Promise<void> {
    try {

      this.newInputEmail=this.userdata.emailAddress;
      //console.log("cureent",this.currentEmail);
     // console.log("newInputEmail",this.newInputEmail);

      if (this.currentEmail !== this.newInputEmail) {
        // If emails are different, call the checkEmailExist API
        const emailCheckResponse = await this.userService.checkEmailExists(this.newInputEmail);
        if (!emailCheckResponse.status) {
          console.error('Error: Email already exists');
          this.dialogRef.close({
            status: false,
            message: emailCheckResponse.message
          });
          return; // Exit early if the email already exists
        }
      }

       // Step 1: If in edit mode, check if the email exists
   /* if (isEditMode) {
      const emailCheckResponse = await this.userService.checkUserNameExists(this.userdata.emailAddress);
      if (!emailCheckResponse.status) {
        console.error('Error: Email already exists');
        this.dialogRef.close({
          status: false,
          message: emailCheckResponse.message
        });
        return; // Exit early if the email already exists
      }
    }*/
    
      // Step 1: Determine whether to update or create the user
      const userResponse = isEditMode
        ? await this.userService.updateUser(this.userdata)
        : await this.userService.createUser(this.userdata);
  
      // Check if the API response has a status of false (error)
      if (userResponse.status === false) {
        // If status is false, log the error and return early to prevent further actions
        console.error('Error:', userResponse.message);
        
        // Close the dialog with the status and message from the API response
        this.dialogRef.close({
          status: false,
          message: userResponse.message || 'An error occurred during the operation.'
        });
        
        return;  // Exit the function early to prevent further execution
      }
  
      // If no error, log the success message
      //console.log(isEditMode ? 'User updated successfully:' : 'User created successfully:', userResponse);
      //console.log(' this.userdata.organizations===', this.userdata.organizations);
      //console.log(' this.userdata===', this.userdata);
  
      // Step 2: Proceed to assign user to organizations after successful user creation/update
     // if (this.role !== 1) {
        if (this.userdata.organizations && this.userdata.organizations.length > 0) {
          for (const orgId of this.userdata.organizations) {
            const userOrgData = {
              userId: isEditMode ? this.userdata.userId : userResponse.id,
              organizationId: orgId,
              roleId:this.userdata.userType
    
            };
      
            try {
              await this.userService.assignOrganization(userOrgData).toPromise();
              console.log(`User assigned to organization ${orgId} successfully.`);
            } catch (error) {
              console.error(`Error assigning user to organization ${orgId}:`, error);
            }
          }
        } else {
          console.warn("No organizations selected. Skipping organization assignment.");
        }
    //  }
  
      // Step 3: Close the dialog with the success response and message
      this.dialogRef.close({ 
        status: true, 
        user: userResponse,  // Pass the userResponse back when closing the dialog
        message: userResponse.message || 'Operation completed successfully'
      });
  
    } catch (error) {
      console.error(isEditMode ? 'Error updating user:' : 'Error creating user:', error);
  
      // Step 4: Handle the error by closing the dialog with an error message
      const errorMessage = error?.message || 'An error occurred during the operation.';
      this.dialogRef.close({
        status: false,
        message: errorMessage  // Pass error message from the exception
      });
    }
     finally {
    // ✅ Always reset the saving flag here
    this.isSaving = false;
  }
  }
  
  
  

  deleteUserOrganizations(organizations: number[]): Observable<any> {
    // Create an observable for each delete request
    const deleteObservables = organizations.map(orgId => {
      return this.userService.deleteUserOrganization(orgId.toString());
    });
  
    // Use forkJoin to ensure all delete operations complete before proceeding
    return forkJoin(deleteObservables);
  }

  onCancel(): void {
    this.dialogRef.close();
  }


  loadOrganizations(): void {
    this.organizationService.getOrganizations().subscribe(
      (data: Organization[]) => {
        this.organizations = data;
  
        // Retrieve the selected organization ID from localStorage
       // const selectedOrganizationId = localStorage.getItem('selectedOrganizationId');
        const selectedOrganizationId =  this.userData.getSelectedOrganization().toString();
        
        if (selectedOrganizationId) {
          // If there's a selected ID in localStorage, filter the organizations
          const selectedOrganization = this.organizations.filter(
            org => org.id.toString() === selectedOrganizationId
          );
  
          if (selectedOrganization.length > 0) {
            // Set the default selection
            this.userdata.organizations = selectedOrganization.map(org => org.id);
            this.selectedOrganizationIds = [...this.userdata.organizations];
            this.organizations = selectedOrganization;  // Show only the selected organization
          }
        } else {
          // If no ID is set in localStorage, show all organizations
          this.selectedOrganizationIds = this.userdata?.organizations ?? [];
          this.organizations = [...this.organizations];  // Show all organizations
        }
  
        // Optionally ensure that the selected organization is reflected in the UI
        this.cdRef.detectChanges(); // Update the view after setting organizations
      },
      (error) => {
        console.error('Error fetching organizations', error);
      }
    );
  }
  
  
  onSelectionChange() {
    // Additional logic if you need to handle anything on selection change
  }

  
 // Method to toggle password visibility
 togglePasswordVisibility() {
  this.showPassword = !this.showPassword;
}
}